<template>
  <div class="work view" ref="view">
    <div class="view-content" ref="viewContent" v-if="!loading">
      <!-- Hero -->
      <work-hero
        ref="hero"
        :image="data.fields.featured_image"
        :key="heroKey"
        :title="data.fields.title"
      />
      <!-- Meta -->
      <div class="push">
        <div class="meta">
          <span class="type">{{ data.fields.type }}</span> /
          <span class="year">{{ data.fields.year }}</span>
          <span class="tags" v-html="tags"></span>
        </div>
        <div class="role" v-html="data.fields.role"></div>
      </div>
      <!-- Intro -->
      <div class="row row-intro">
        <div class="col col-text push">
          <div class="intro" ref="intro" v-html="data.fields.intro"></div>
        </div>
        <div class="col col-aside">
          <work-responsibilities
            ref="responsibilities"
            :responsibilities="data.fields.responsibilities"
            :key="responsibilitiesKey"
          />
        </div>
      </div>
      <!-- Devices -->
      <work-devices
        v-if="showDevices"
        style="margin-bottom: 4rem"
        :controller="controller"
        :screenshotDesktop="data.fields.screenshot_desktop"
        :screenshotTablet="data.fields.screenshot_tablet"
        :screenshotMobile="data.fields.screenshot_mobile"
        :key="devicesKey"
        ref="devices"
      ></work-devices>
      <!-- Description -->
      <div class="row">
        <div class="col col-text">
          <div class="description">
            <div v-html="data.fields.description"></div>
            <a
              v-if="data.fields.site_url"
              class="site-link"
              :href="data.fields.site_url"
              target="_blank"
              rel="noopener nofollow"
            >Visit site</a>
          </div>
        </div>
        <!-- Stats -->
        <div v-if="data.fields.stats" class="col col-aside col-left">
          <div class="work-stats">
            <work-stat
              v-for="stat in data.fields.stats"
              :legend="stat.legend"
              :number="stat.number"
              :key="stat.id"
              ref="stat"
            />
          </div>
        </div>
      </div>
      <!-- Screenshots -->
      <work-screenshots
        v-if="showScreenshots"
        :style="{ marginBottom: '4rem' }"
        :data="data.fields.screenshots"
        :key="screenshotsKey"
        ref="screenshots"
      ></work-screenshots>
      <!-- Next work -->
      <div v-if="nextWork" class="next-work">
        <div class="heading">
          <div class="text">Next work</div>
        </div>
        <works-list :data="[nextWork]" :key="nextWorkKey"/>
      </div>
      <div v-else style="text-align: center">
        <cta-link
          style="margin: 0 auto"
          :bgColor="'black'"
          :emoji="'👈'"
          :hoverColor="data.fields.color"
          :to="{ 
            name: 'works', 
          }"
        >&larr; All works</cta-link>
      </div>
    </div>
  </div>
</template>

<script>
import utils from "@/utils";
import { api, imageApi } from "@/api";
import ScrollMagic from "scrollmagic";
import { TweenMax, TimelineMax } from "gsap/TweenMax";
import "imports-loader?define=>false!scrollmagic/scrollmagic/uncompressed/plugins/animation.gsap";
import "imports-loader?define=>false!@/assets/vendor/SplitText";
import VueScrollTo from "vue-scrollto";

import WorkHero from "@/components/WorkHero";
import WorkResponsibilities from "@/components/WorkResponsibilities";
import WorkDevices from "@/components/WorkDevices";
import WorkScreenshots from "@/components/WorkScreenshots";
import WorkStat from "@/components/WorkStat";
import WorksList from "@/components/WorksList";
import CtaLink from "@/components/CtaLink";

export default {
  components: {
    WorkHero,
    WorkResponsibilities,
    WorkStat,
    WorkDevices,
    WorkScreenshots,
    WorksList,
    CtaLink
  },
  data() {
    return {
      data: null,
      heroTl: null,
      controller: null
    };
  },
  computed: {
    loading() {
      return this.data === null;
    },
    heroKey() {
      return `${this.data.slug}-hero`;
    },
    nextWorkKey() {
      return `${this.data.slug}-next`;
    },
    devicesKey() {
      return `${this.data.slug}-devices`;
    },
    screenshotsKey() {
      return `${this.data.slug}-screenshots`;
    },
    responsibilitiesKey() {
      return `${this.data.slug}-responsibilities`;
    },
    windowWidth() {
      return this.$store.state.windowSize.width;
    },
    seoData() {
      return {
        title: this.loading
          ? "Loading..."
          : this.data.fields.seo_title
          ? this.data.fields.seo_title
          : this.data.fields.title,
        description: this.loading
          ? "Loading..."
          : this.data.fields.seo_meta
          ? this.data.fields.seo_meta
          : this.data.fields.title,
        template: "🦄 Antti Korhonen",
        url: `https://www.akworks.co${this.$route.path}`,
        image: this.loading
          ? ""
          : imageApi.getUrl(this.data.fields.featured_image, {
              w: 1200,
              h: 630,
              b: `rgb:${this.data.fields.color.substr(1)}`,
              c: "lpad",
              g: "center"
            })
      };
    },
    nextWork() {
      if (this.data.slug) {
        return this.getNextBySlug(this.data.slug);
      }
    },
    getNextBySlug() {
      return this.$store.getters[`getNextBySlug`];
    },
    showDevices() {
      return (
        this.data.fields.screenshot_desktop &&
        this.data.fields.screenshot_tablet &&
        this.data.fields.screenshot_mobile
      );
    },
    showScreenshots() {
      return this.data.fields.screenshots.length > 0;
    },
    tags() {
      return this.data.fields.tags
        .map((item, index) => {
          return index < this.data.fields.tags.length - 1
            ? `<span>${item.name}</span> + `
            : `<span>${item.name}</span>`;
        })
        .join("");
    }
  },
  methods: {
    setData(data) {
      this.data = data;
      utils.setColor(this.data.fields.color);
      this.$nextTick(() => {
        this.initScroll();
      });
    },
    showView(cb) {
      TweenMax.to(this.$refs.viewContent, 0.3, {
        opacity: 1,
        ease: Cubic.easeInOut,
        onComplete: cb
      });
    },
    hideView(cb) {
      TweenMax.to(this.$refs.viewContent, 0.3, {
        opacity: 0,
        ease: Cubic.easeInOut,
        onComplete: cb
      });
    },
    initScroll() {
      this.controller = new ScrollMagic.Controller();

      // Stats
      if (this.data.fields.stats.length > 0) {
        this.$refs.stat.forEach(stat => {
          const el = stat.$el;
          new ScrollMagic.Scene({
            triggerElement: el,
            triggerHook: 0.7,
            reverse: false
          })
            .on("start", () => {
              stat.reveal();
            })
            .addTo(this.controller);
        });
      }

      // Responsibilities
      const { responsibilities } = this.$refs;
      const { title, responsibility } = responsibilities.$refs;

      new ScrollMagic.Scene({
        triggerElement: responsibilities.$el,
        triggerHook: 0.7,
        reverse: false
      })
        .setTween(
          TweenMax.staggerFrom(
            [title, ...responsibility],
            0.5,
            { opacity: 0, y: "-30", ease: Cubic.easeOut },
            0.1
          )
        )
        .addTo(this.controller);

      // Screenshots
      if (this.showScreenshots) {
        const { screenshots } = this.$refs;
        const { screenshot } = screenshots.$refs;

        screenshot.forEach(shot => {
          new ScrollMagic.Scene({
            triggerElement: shot,
            triggerHook: 0.7,
            reverse: false
          })
            .setTween(
              TweenMax.from(shot, 0.7, {
                opacity: 0,
                y: 30,
                ease: Cubic.easeInOut
              })
            )
            .addTo(this.controller);
        });
      }
    },
    destroyScroll() {
      if (this.controller) {
        this.controller.destroy(true);
        this.controller = null;
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    api
      .getWorkBySlug(to.params.slug)
      .then(res => {
        next(vm => vm.setData(res.data.data));
      })
      .catch(error => {
        console.log(error.message);
      });
  },
  beforeRouteUpdate(to, from, next) {
    this.hideView(() => {
      this.destroyScroll();
      window.scrollTo(0, 0);
      api
        .getWorkBySlug(to.params.slug)
        .then(res => {
          this.setData(res.data.data);
          this.showView();
          next();
        })
        .catch(error => {
          console.log(error.message);
        });
    });
  },
  beforeRouteLeave(to, from, next) {
    this.hideView(next);
  },
  beforeDestroy() {
    this.destroyScroll();
  },
  metaInfo() {
    return {
      title: this.seoData.title,
      titleTemplate: titleChunk =>
        titleChunk
          ? `${titleChunk} ${this.seoData.template}`
          : this.seoData.template,
      meta: [
        {
          name: "description",
          vmid: "description",
          content: this.seoData.description
        },
        {
          property: "og:title",
          vmid: "og:title",
          content: this.seoData.title,
          template: chunk => `${chunk} ${this.seoData.template}`
        },
        {
          property: "og:description",
          vmid: "og:description",
          content: this.seoData.description
        },
        {
          property: "og:type",
          vmid: "og:type",
          content: "article"
        },
        {
          property: "og:image",
          vmid: "og:image",
          content: this.seoData.image
        },
        {
          property: "og:url",
          vmid: "og:url",
          content: this.seoData.url
        }
      ]
    };
  }
};
</script>

<style lang="scss">
.role a {
  text-decoration: none;
  color: var(--primary-color);
}
.row {
  @media (min-width: 768px) {
    display: flex;
  }
}
.col {
  order: 2;
  margin-top: 2.5rem;
  margin-bottom: 2.5rem;

  @media (min-width: 768px) {
    margin-top: 4rem;
    margin-bottom: 4rem;
  }
}
.col-aside {
  @media (min-width: 768px) {
    margin-left: 12.5%;
    flex: 0 0 25%;
  }
}
.col-right {
  @media (min-width: 768px) {
    order: 2;
  }
}
.col-left {
  @media (min-width: 768px) {
    order: 1;
  }
}
.col-text {
  @media (min-width: 768px) {
    flex: 0 0 62.5%;
  }
}

.push {
  @media (min-width: 1024px) {
    padding-left: 12.5%;
  }
}
</style>

<style lang="scss" scoped>
.meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: -0.5rem;
  font-size: 1.1rem;

  @media (min-width: 768px) {
    font-size: 1.2rem;
  }

  .type,
  .year {
    font-weight: bold;
  }

  span {
    display: inline-block;
  }

  & > * {
    margin: 0.5rem;
  }
}
.tags {
  display: flex;
  flex-wrap: wrap;
}
.role {
  margin-top: 0.5rem;
  font-size: 1.1rem;

  @media (min-width: 768px) {
    margin-top: 1.5rem;
    font-size: 1.2rem;
  }
}

.intro {
  position: relative;
  padding-left: 1rem;

  @media (min-width: 768px) {
    padding-left: 2rem;
    font-size: 1.3rem;
    flex: 0 0 62.5%;
  }
  @media (min-width: 1024px) {
    font-size: 1.5rem;
  }

  &:before {
    content: "";
    position: absolute;
    left: -3px;
    top: 0;
    bottom: 0;
    border-left: 3px solid var(--primary-color, #000000);

    @media (min-width: 768px) {
      left: 0;
      border-left-width: 5px;
    }
  }
}

.description {
  position: relative;
  padding-left: 1rem;

  @media (min-width: 768px) {
    padding-left: 2rem;
  }

  &:before {
    content: "";
    position: absolute;
    left: -3px;
    top: 0;
    bottom: 0;
    border-left: 3px solid #eee;

    @media (min-width: 768px) {
      left: 0;
      border-left-width: 5px;
    }
  }
}

.site-link {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: bold;
  display: inline-block;

  div + & {
    margin-top: 1.5rem;
  }

  &::after {
    content: "\2192";
    display: inline-block;
    margin-left: 0.5rem;
    transition: transform 0.3s ease-in-out;
  }

  &:hover:after {
    transform: translateX(10px);
  }
}

.work-stats {
  margin: -0.5rem 0;
}

.next-work {
  margin-top: 8rem;

  .heading {
    position: relative;
    display: flex;
    height: 20px;
    margin-bottom: -10px;

    .text {
      position: relative;
      z-index: 1;
      flex: 0 0 37.5%;
      margin-left: 12.5%;
      text-align: center;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 12.5%;
      height: 50%;
      border-bottom: 1px solid #f4f4f4;
    }
    &:after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      width: 62.5%;
      height: 50%;
      border-bottom: 1px solid #f4f4f4;
    }

    @media (min-width: 640px) {
      .text {
        flex-basis: 25%;

        &:after {
          width: 37.5%;
        }
      }
    }
    @media (min-width: 960px) {
      .text {
        flex-basis: 12.5%;

        &:after {
          width: 75%;
        }
      }
    }
  }
}
</style>